import { useEffect, useState, useMemo, useCallback } from 'react';
import { Country } from '@tiendanube/common/src/enums';
import { FS_NUVEMPAGO_NUDGES_VARIANT } from 'App/featuresFlags';
import { getStorage, setStorage } from 'App/storage';
import { NudgeType, NUDGE_EXPIRATION_TIME } from 'commons/constants';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import { useGetCountry, useIsLogged } from 'domains/Auth/hooks';
import { useEvaluationResult } from 'domains/NuvemPagoCommons/hooks/useEvaluationResult';
import {
  useNuvemPagoIsActive,
  useInstalledPaymentProviders,
} from 'domains/Payments/PaymentProviders/hooks';

interface NudgeState {
  timestamp: number;
  isVisible: boolean;
}

type ComponentType = 'cta' | 'callout' | 'none';

type Group = 'none' | 'group1' | 'group2' | 'group3' | 'group4';

interface ControlCtaReturn {
  componentType: ComponentType;
  isCtaVisible: boolean;
  isCalloutVisible: boolean;
  handleDismissCta: () => void;
  handleDismissCallout: () => void;
  group: Group;
}

const INITIAL_STATE: NudgeState = {
  timestamp: Date.now(),
  isVisible: true,
};

const SEGMENT_COMPONENT_MAP: Record<number, ComponentType> = {
  0: 'none',
  3: 'cta',
  4: 'callout',
  5: 'callout',
};

const GROUP_BY_SEGMENT: Record<number, Group> = {
  0: 'none',
  3: 'group2',
  4: 'group3',
  5: 'group4',
};

const useNuvemPagoIsActiveWithRefetchOnFocus = () => {
  const nuvemPagoResult = useNuvemPagoIsActive();
  const { fetchInstalledPaymentProviders } = useInstalledPaymentProviders();

  useEffect(() => {
    const handleFocus = () => {
      fetchInstalledPaymentProviders();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchInstalledPaymentProviders]);

  return nuvemPagoResult;
};

export const useValidation = () => {
  const storeCountry = useGetCountry();
  const { isLogin } = useIsLogged();
  const { validateRoles } = useRoles();
  const { nuvemPagoIsActive } = useNuvemPagoIsActiveWithRefetchOnFocus();

  return useMemo(
    () => ({
      shouldSkipQuery:
        (!isLogin ||
          storeCountry !== Country.BR ||
          !validateRoles(['full', 'payments']) ||
          nuvemPagoIsActive) ??
        true,
    }),
    [isLogin, storeCountry, validateRoles, nuvemPagoIsActive],
  );
};

export const useNudgeStates = () => {
  const [ctaState, setCtaState] = useState<NudgeState>(INITIAL_STATE);
  const [calloutState, setCalloutState] = useState<NudgeState>(INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadNudgeStates = async () => {
      try {
        const [storedCtaState, storedCalloutState] = await Promise.all([
          getStorage<NudgeState>(`nudge_${NudgeType.CTA}`),
          getStorage<NudgeState>(`nudge_${NudgeType.CALLOUT}`),
        ]);
        if (storedCtaState) setCtaState(storedCtaState);
        if (storedCalloutState) setCalloutState(storedCalloutState);
      } finally {
        setIsLoading(false);
      }
    };
    loadNudgeStates();
  }, []);

  return { ctaState, calloutState, isLoading, setCtaState, setCalloutState };
};

export const useControlCta = (): ControlCtaReturn => {
  const { shouldSkipQuery } = useValidation();
  const {
    ctaState,
    calloutState,
    isLoading: isLoadingNudge,
    setCtaState,
    setCalloutState,
  } = useNudgeStates();

  const { data: evaluationData, isLoading: isLoadingEvaluation } =
    useEvaluationResult(FS_NUVEMPAGO_NUDGES_VARIANT, {
      skip: shouldSkipQuery,
      refetchOnFocus: true,
    });

  const handleDismissNudge = useCallback(
    async (type: NudgeType) => {
      const newState = { timestamp: Date.now(), isVisible: false };
      type === NudgeType.CTA
        ? setCtaState(newState)
        : setCalloutState(newState);
      await setStorage(`nudge_${type}`, newState);
    },
    [setCtaState, setCalloutState],
  );

  const shouldShowNudge = useCallback(
    (state: NudgeState): boolean =>
      state.isVisible || Date.now() - state.timestamp >= NUDGE_EXPIRATION_TIME,
    [],
  );

  const handleDismissCta = useCallback(
    () => handleDismissNudge(NudgeType.CTA),
    [handleDismissNudge],
  );
  const handleDismissCallout = useCallback(
    () => handleDismissNudge(NudgeType.CALLOUT),
    [handleDismissNudge],
  );

  const emptyReturn = useMemo(
    (): ControlCtaReturn => ({
      componentType: 'none',
      isCtaVisible: false,
      isCalloutVisible: false,
      handleDismissCta,
      handleDismissCallout,
      group: 'none',
    }),
    [handleDismissCta, handleDismissCallout],
  );

  const isLoading = useMemo(
    () =>
      isLoadingNudge ||
      isLoadingEvaluation ||
      !evaluationData?.feature_enabled ||
      !evaluationData?.strategy_enabled,
    [
      isLoadingNudge,
      isLoadingEvaluation,
      evaluationData?.feature_enabled,
      evaluationData?.strategy_enabled,
    ],
  );

  const segment = useMemo(
    () => evaluationData?.segments?.[0],
    [evaluationData],
  );
  const componentType = useMemo(
    () => (segment ? SEGMENT_COMPONENT_MAP[segment] || 'none' : 'none'),
    [segment],
  );

  const group = useMemo(
    () => (segment ? GROUP_BY_SEGMENT[segment] || 'none' : 'none'),
    [segment],
  );

  const result = useMemo((): ControlCtaReturn => {
    if (isLoading) {
      return emptyReturn;
    }

    return {
      componentType,
      isCtaVisible: componentType === 'cta' && shouldShowNudge(ctaState),
      isCalloutVisible:
        componentType === 'callout' && shouldShowNudge(calloutState),
      handleDismissCta,
      handleDismissCallout,
      group,
    };
  }, [
    isLoading,
    emptyReturn,
    componentType,
    shouldShowNudge,
    ctaState,
    calloutState,
    handleDismissCta,
    handleDismissCallout,
    group,
  ]);
  return result;
};
